import { useEffect, useMemo, useState } from "react";
import { api, inr } from "@/lib/api";
import { PageHeader } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchablePicker, { productMatch, renderProductRow } from "@/components/app/SearchablePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PriceEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pf, setPf] = useState("all");
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [e, p, b, v] = await Promise.all([
      api.get("/purchasing/price-enquiries"),
      api.get("/purchasing/products"),
      api.get("/purchasing/brands"),
      api.get("/purchasing/vendors"),
    ]);
    setEnquiries(e.data); setProducts(p.data); setBrands(b.data); setVendors(v.data);
  };
  useEffect(() => { load(); }, []);

  const productName = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p.name])), [products]);
  const brandName = useMemo(() => Object.fromEntries(brands.map((b) => [b.id, b.name])), [brands]);
  const vendorName = useMemo(() => Object.fromEntries(vendors.map((v) => [v.id, v.name])), [vendors]);

  const rows = pf === "all" ? enquiries : enquiries.filter((e) => e.product_id === pf);

  const del = async (e) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try { await api.delete(`/purchasing/price-enquiries/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="pv-enquiries-page">
      <PageHeader
        eyebrow="Purchasing"
        title="Price Enquiry Register"
        subtitle="Record every vendor quotation, even when you don't purchase. Builds price history over time."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="pv-new-enquiry-btn">
                <Plus className="w-4 h-4 mr-2" /> Record Quotation
              </Button>
            </DialogTrigger>
            {open && <EnquiryDialog products={products} brands={brands} vendors={vendors} onDone={() => { setOpen(false); load(); }} />}
          </Dialog>
        }
      />
      <div className="p-4 sm:p-8">
        <div className="mb-4">
          <Select value={pf} onValueChange={setPf}>
            <SelectTrigger className="rounded-none h-10 w-64" data-testid="pv-enquiry-product-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
              <tr>{["Date", "Product", "Brand", "Vendor", "Quoted", "GST", "Delivery", "Remarks", ""].map((h, i) => (
                <th key={i} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`pv-enquiry-row-${e.id}`}>
                  <td className="px-4 py-3 text-xs font-mono">{e.enquiry_date}</td>
                  <td className="px-4 py-3 text-xs">{productName[e.product_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs">{brandName[e.brand_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs font-semibold">{vendorName[e.vendor_id] || "—"}</td>
                  <td className="px-4 py-3 text-xs font-mono">{inr(e.quoted_price)}{e.unit_of_measure ? `/${e.unit_of_measure}` : ""}</td>
                  <td className="px-4 py-3 text-xs">{e.gst_included ? "incl." : "excl."}</td>
                  <td className="px-4 py-3 text-xs">{e.delivery_time_days ? `${e.delivery_time_days} d` : "—"}</td>
                  <td className="px-4 py-3 text-xs text-[#4B5563] max-w-xs truncate" title={e.remarks}>{e.remarks || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => del(e)} className="text-[#9CA3AF] hover:text-[#DC2626]" data-testid={`pv-enquiry-del-${e.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="text-center text-sm text-[#9CA3AF] py-12">No enquiries recorded yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function EnquiryDialog({ products, brands, vendors, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({
    enquiry_date: today, product_id: products[0]?.id || "", brand_id: "",
    vendor_id: vendors[0]?.id || "", unit_of_measure: products[0]?.unit_of_measure || "",
    quoted_price: "", gst_included: false, transport_included: false,
    delivery_time_days: "", payment_terms: "", warranty: "", remarks: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const brandsForProduct = brands.filter((b) => b.product_id === f.product_id);

  const submit = async () => {
    if (!f.product_id || !f.vendor_id) { toast.error("Product and vendor required"); return; }
    if (!f.quoted_price) { toast.error("Enter quoted price"); return; }
    try {
      await api.post("/purchasing/price-enquiries", {
        ...f,
        brand_id: f.brand_id || null,
        quoted_price: parseFloat(f.quoted_price),
        delivery_time_days: f.delivery_time_days ? parseInt(f.delivery_time_days, 10) : null,
      });
      toast.success("Quotation recorded"); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Record Vendor Quotation</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Enquiry Date</Label><Input type="date" value={f.enquiry_date} onChange={set("enquiry_date")} className="rounded-none mt-2 h-10" data-testid="pv-e-date" /></div>
          <div><Label className="label-eyebrow">Vendor *</Label>
            <Select value={f.vendor_id} onValueChange={(v) => setF({ ...f, vendor_id: v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-e-vendor"><SelectValue /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Product *</Label>
            <SearchablePicker
              items={products}
              value={f.product_id}
              onChange={(v) => {
                const p = products.find((x) => x.id === v);
                setF({ ...f, product_id: v, brand_id: "", unit_of_measure: p?.unit_of_measure || "" });
              }}
              placeholder="Search product…"
              testid="pv-e-product"
              selectedLabel={(p) => p.name}
              matchItem={productMatch}
              renderRow={renderProductRow}
            />
          </div>
          <div><Label className="label-eyebrow">Brand</Label>
            <Select value={f.brand_id || "_none"} onValueChange={(v) => setF({ ...f, brand_id: v === "_none" ? "" : v })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="pv-e-brand"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— none —</SelectItem>
                {brandsForProduct.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="label-eyebrow">Quoted Price ₹ *</Label><Input type="number" step="0.01" value={f.quoted_price} onChange={set("quoted_price")} className="rounded-none mt-2 h-10" data-testid="pv-e-price" /></div>
          <div><Label className="label-eyebrow">UoM</Label><Input value={f.unit_of_measure} onChange={set("unit_of_measure")} className="rounded-none mt-2 h-10" /></div>
          <div><Label className="label-eyebrow">Delivery (days)</Label><Input type="number" value={f.delivery_time_days} onChange={set("delivery_time_days")} className="rounded-none mt-2 h-10" /></div>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.gst_included} onChange={(e) => setF({ ...f, gst_included: e.target.checked })} data-testid="pv-e-gst" /> GST included</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={f.transport_included} onChange={(e) => setF({ ...f, transport_included: e.target.checked })} data-testid="pv-e-transport" /> Transport included</label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="label-eyebrow">Payment Terms</Label><Input value={f.payment_terms} onChange={set("payment_terms")} className="rounded-none mt-2 h-10" /></div>
          <div><Label className="label-eyebrow">Warranty</Label><Input value={f.warranty} onChange={set("warranty")} className="rounded-none mt-2 h-10" /></div>
        </div>
        <div><Label className="label-eyebrow">Remarks</Label><Textarea value={f.remarks} onChange={set("remarks")} rows={2} className="rounded-none mt-2" /></div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="pv-e-submit">Save Quotation</Button>
      </div>
    </DialogContent>
  );
}
